import { Form } from '@/components/ui/form';
import { RouteParams } from '@/router/type';
import { useGetServerlessEndpointById } from '@/service/serverless-gpu/hook';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { chatConversationSchema, ChatConversationSchema } from './type';
import { zodResolver } from '@hookform/resolvers/zod';
import PlaygroundTextGenerationSetting from './setting';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { fEndpointServerless } from '@/utils/formatLabel';
import { models } from './models';
import MessageInput from '@/features/chat/message-input';
import {
  ChatMessage,
  ChatMessageAvatar,
  ChatMessageContent,
} from '@/components/ui/chat-message';
import { useChatStream } from './useChatStream';
import { toastSomethingError } from '@/components/ui/toast-content';
import {
  BotMessageSquareIcon,
  RotateCcwIcon,
  SlidersHorizontalIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import React, { useEffect, useState } from 'react';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';
import { ReloadIcon } from '@radix-ui/react-icons';

const ServerlessGPUPlaygroundBlueprintLLM = () => {
  const { projectId } = useParams<RouteParams>();
  const { data: dataEndpoint } =
    useGetServerlessEndpointById({
      project_id: projectId!,
    });

  const endpoint = fEndpointServerless(dataEndpoint?.endpoint || '').function;
  const dataModels = useQuery({
    queryKey: ['data-models', dataEndpoint?.endpoint],
    queryFn: async () => {
      const { data } = await axios.get(`${endpoint}/models`, {
        headers: {
          'Authorization': `Bearer ${dataEndpoint?.api_key}`
        }
      });
      return data;
    },
    staleTime: 1000 * 60 * 5,
    enabled: !!dataEndpoint
  });

  useEffect(() => {
    if (dataModels?.data?.data?.length > 0) {
      form.setValue('models', dataModels?.data?.data[0]?.id);
    }
  }, [dataModels?.data?.data]);

  const form = useForm<ChatConversationSchema>({
    resolver: zodResolver(chatConversationSchema),
    defaultValues: {
      models: '',
      temperature: 0.5,
      maxTokens: 2000,
    },
  });

  
  const { messages, append, isStreaming, reset } = useChatStream({
    model: form.watch('models'),
    apiKey: dataEndpoint?.api_key || '',
    endpoint: endpoint,
    onError: (err) => {
      toastSomethingError(err);
    },
    onSuccess: () => {},
    options: {
      temperature: form.watch('temperature'),
      max_completion_tokens: form.watch('maxTokens'),
    },
    systemPrompt: form.watch('systemPrompt'),
  });

  const onSubmit = async(input: string) => {
    const isFormValid = await form.trigger()
    if(!isFormValid) return;
    append({
      role: 'user',
      content: input,
    });
  };

  const [isSettingOpen, setIsSettingOpen] = useState(true);

  const RenderSliderSetting = () => {
    return (
      <Button
        variant={'outline'}
        size={'icon'}
        onClick={() => setIsSettingOpen((prev) => !prev)}
        aria-expanded={isSettingOpen}
        aria-controls="playground-setting"
      >
        <SlidersHorizontalIcon size={16} />
      </Button>
    );
  };

  return (
    <div className="container mt-4">
      <Form {...form}>
        <form className="flex flex-grow">
          <div className="h-full w-full">
            <div className="box-border flex items-stretch justify-start gap-4">
              <div className="box-border flex h-[calc(100dvh-168px)] w-full min-w-0 grow flex-col items-stretch justify-start gap-4">
                <div className="flex h-8 items-center justify-between">
                  <div className="flex items-center gap-1">
                    <BotMessageSquareIcon />
                    <p className="text-xl font-semibold">Chat</p>
                  </div>
                  <div className="flex gap-4">
                    {messages.length > 0 && (
                      <Button
                        variant={'link'}
                        size={'sm'}
                        onClick={() => reset()}
                        endIcon={<RotateCcwIcon size={14} />}
                        className="px-0"
                      >
                        Clear Session
                      </Button>
                    )}
                    {!isSettingOpen && <RenderSliderSetting />}
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto rounded-xl border">
                  <div className="space-y-4 p-4">
                    {messages.map((message, idx) => {
                      if (message.role === 'user') {
                        return (
                          <ChatMessage
                            key={idx}
                            id={idx.toString()}
                            type={'outgoing'}
                            variant="bubble"
                          >
                            <div className="flex items-center gap-2">
                              <ChatMessageContent
                                content={message.content as string}
                              />
                            </div>
                          </ChatMessage>
                        );
                      }
                      if (message.role === 'assistant') {
                        return (
                          <ChatMessage
                            key={idx}
                            id={idx.toString()}
                            type={'incoming'}
                            // variant="bubble"
                          >
                            <ChatMessageAvatar />
                            <div className="flex flex-col gap-2">
                              <ChatMessageContent
                                content={message.content as string}
                              ></ChatMessageContent>
                            </div>
                          </ChatMessage>
                        );
                      }
                    })}
                  </div>
                </div>
                <div className="">
                  <MessageInput
                    onSearch={onSubmit}
                    isLoading={isStreaming}
                    placeholder="Type something..."
                  />
                </div>
              </div>
              <Collapsible open={isSettingOpen} onOpenChange={setIsSettingOpen}>
                <CollapsibleContent id="playground-setting">
                  <div className="box-border flex h-[calc(100dvh-168px)] w-[304px] min-w-0 grow flex-col items-stretch justify-start gap-4">
                    <div className="flex h-8 items-center justify-end">
                      <RenderSliderSetting />
                    </div>

                    <div className="min-h-0 flex-1 overflow-clip rounded-xl border">
                      <div className="h-full overflow-auto p-4">
                        <div className="box-border flex flex-1 flex-col items-stretch justify-start gap-2">
                          <PlaygroundTextGenerationSetting
                            // models={models?.map((item) => item.id)}
                            models={dataModels?.data?.data?.map((item:any) => item.id)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default ServerlessGPUPlaygroundBlueprintLLM;
